import type { InstrumentFormState } from '@/config/instruments/formUtils';
import type { Org } from '@/types';
import { parseDateTime } from '@internationalized/date';
import { DatePicker } from '@nautilusbio/nb-design-system/DatePicker';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Select } from '@nautilusbio/nb-design-system/Select';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Switch } from '@nautilusbio/nb-design-system/Switch';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { useRef, useMemo } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.css';

interface InstrumentFormDialogContentProps {
  form: InstrumentFormState;
  errors: Partial<Record<keyof InstrumentFormState, string>>;
  // Use the same setter signature as the form hook
  setForm: (key: keyof InstrumentFormState, value: InstrumentFormState[keyof InstrumentFormState]) => void;
  userOrganizations: Org[];
  selectedOrgId: string;
  onOrgChange: (orgId: string) => void;
}

const InstrumentFormDialogContentComponent: FC<InstrumentFormDialogContentProps> = ({
  form,
  errors,
  setForm,
  userOrganizations,
  selectedOrgId,
  onOrgChange,
}) => {
  const { t } = useTranslation('instruments');
  const containerRef = useRef<HTMLDivElement>(null);

  const orgOptions = useMemo(() => userOrganizations.map(org => org.name), [userOrganizations]);
  const orgIds = useMemo(() => userOrganizations.map(org => org.id), [userOrganizations]);
  const selectedOrgIndex = useMemo(() => orgIds.indexOf(selectedOrgId), [orgIds, selectedOrgId]);

  const handleOrgChange = (index: number) => {
    if (index >= 0 && index < orgIds.length) {
      onOrgChange(orgIds[index]);
    }
  };

  return (
    <Stack gap={4} orientation="vertical" ref={containerRef}>
      {/* Name */}
      <Stack gap={1} orientation="vertical">
        <Input
          autoComplete="off"
          data-testid="input-instrument-name"
          fullWidth
          id="instrument-name"
          label={{ children: t('field_name'), htmlFor: 'instrument-name', id: 'label-instrument-name' }}
          name="name"
          value={form.name}
          onChange={e => setForm('name', e.target.value)}
          placeholder={t('field_name_placeholder')}
        />
        {errors.name && (
          <Text color="error" size="sm">
            {errors.name}
          </Text>
        )}
      </Stack>

      {/* Serial Number */}
      <Input
        autoComplete="off"
        data-testid="input-instrument-serial"
        fullWidth
        id="instrument-serial"
        label={{ children: t('field_serial_num'), htmlFor: 'instrument-serial', id: 'label-instrument-serial' }}
        name="serial_num"
        value={form.serial_num}
        onChange={e => setForm('serial_num', e.target.value)}
        placeholder={t('field_serial_num_placeholder')}
      />

      {/* Row 3: Model (60%) + Organization (40%) */}
      <Stack display="grid" gridTemplateColumns="3fr 2fr" gap={4} fullWidth>
        <Input
          autoComplete="off"
          data-testid="input-instrument-model"
          fullWidth
          id="instrument-model"
          label={{ children: t('field_model'), htmlFor: 'instrument-model', id: 'label-instrument-model' }}
          name="model"
          value={form.model}
          onChange={e => setForm('model', e.target.value)}
          placeholder={t('field_model_placeholder')}
        />
        <Select
          allowClear={false}
          className={styles.selectFullWidth}
          closeOnSelect
          data-testid="select-instrument-org"
          id="instrument-org"
          isDisabled={userOrganizations.length <= 1}
          label={{
            children: t('field_organization'),
            htmlFor: 'instrument-org',
            id: 'label-instrument-org',
          }}
          onChange={handleOrgChange}
          options={orgOptions}
          root={containerRef}
          value={selectedOrgIndex >= 0 ? selectedOrgIndex : 0}
        />
      </Stack>

      {/* Row 4: Available (50%, start) + Next PM (50%, end) */}
      <Stack display="grid" gridTemplateColumns="1fr 1fr" gap={4} fullWidth alignItems="center">
        <Switch
          checked={form.is_available}
          className={styles.switchStart}
          data-testid="switch-instrument-available"
          id="instrument-is-available"
          label={t('field_is_available')}
          name="is_available"
          onChange={checked => setForm('is_available', checked)}
        />
        <DatePicker
          className={styles.datePickerEnd}
          data-testid="input-instrument-pm"
          granularity="minute"
          label={{
            children: t('field_next_pm_at'),
            htmlFor: 'instrument-next-pm',
            id: 'label-instrument-next-pm',
            hasPlaceholder: false,
            isFocused: false,
            hasValue: !!form.next_pm_at,
          }}
          value={
            form.next_pm_at
              ? (() => {
                  try {
                    return parseDateTime(form.next_pm_at);
                  } catch {
                    return null;
                  }
                })()
              : null
          }
          onChange={dateValue => setForm('next_pm_at', dateValue ? dateValue.toString() : '')}
        />
      </Stack>
    </Stack>
  );
};

export const InstrumentFormDialogContent = React.memo(InstrumentFormDialogContentComponent);
